import * as React from 'react';
import { Image } from '@/hen-ui/atomics/Image';
import { Text } from '@/hen-ui/atomics/Text';
import { Link } from '@/hen-ui/atomics/Link';
import { Separator } from '@/hen-ui/atomics/Separator';
import { Card } from '@/hen-ui/composites/Card';
import { Flex } from '@/hen-ui/composites/Flex';
import { SectionHeader } from '@/hen-ui/composites/SectionHeader';
import {
  articleCardContainerVariants,
  articleCardImageVariants,
} from './article-card.variants';

export interface ArticleCardProps {
  /** Featured image URL - required */
  image: string;
  /** Alt text for the image - required */
  imageAlt: string;
  /** Article title heading - required */
  title: string;
  /** Purple eyebrow/category text above title - optional */
  brow?: string;
  /** Author name (prefixed with "by") - optional */
  author?: string;
  /** Publication date string - optional */
  date?: string;
  /** Link URL for the card - optional */
  href?: string;
  /** Size variant - default: "lg" */
  size?: 'lg' | 'md' | 'sm' | 'compact';
  /** Border variant - default: "none" */
  border?: 'none' | 'padding' | 'stroke' | 'shadow';
}

/**
 * ArticleCard component following HEN Design System standards.
 *
 * A composite component that combines Image, Heading, and Text atomics to display
 * article previews with featured image, brow text, title, author, and date.
 * Standardizes article preview displays across HEN applications.
 *
 * @example
 * // Large card with all content
 * <ArticleCard
 *   image="/images/article.jpg"
 *   imageAlt="Article preview"
 *   brow="Technology"
 *   title="The Future of Online Learning"
 *   author="Jane Doe"
 *   date="September 25, 2025"
 *   href="/articles/future-of-learning"
 *   size="lg"
 * />
 *
 * @example
 * // Compact horizontal layout
 * <ArticleCard
 *   image="/images/article-thumb.jpg"
 *   imageAlt="Article thumbnail"
 *   title="Quick Read Article Title"
 *   author="John Smith"
 *   date="October 1, 2025"
 *   href="/articles/quick-read"
 *   size="compact"
 * />
 */
export const ArticleCard = React.forwardRef<HTMLDivElement, ArticleCardProps>(
  (
    {
      image,
      imageAlt,
      title,
      brow,
      author,
      date,
      href,
      size = 'lg',
      border = 'none',
    },
    ref,
  ) => {
    // Map size variants to heading sizes
    const headingSize = {
      lg: 'h4' as const,
      md: 'h5' as const,
      sm: 'h6' as const,
      compact: 'h6' as const,
    }[size];

    // Determine layout and gap based on size
    const isCompact = size === 'compact';
    const layout = isCompact ? 'horizontal' : 'vertical';
    const containerGap = {
      lg: 'md' as const,
      md: 'md' as const,
      sm: 'sm' as const,
      compact: 'sm' as const,
    }[size];

    // Set card variant based on border - transparent when no border, white otherwise
    const cardVariant =
      border === 'none' ? 'background-transparent' : 'background-white';

    // Shared card content
    const cardContent = (
      <>
        <Image
          src={image}
          alt={imageAlt}
          loading='lazy'
          decoding='async'
          className={articleCardImageVariants({ size, border })}
        />
        <Card.Content className='px-4 py-2'>
          <SectionHeader
            size={headingSize}
            title={title}
            brow={brow}
            align='left'
          />
          {(author || date) && (
            <Flex direction='row' gap='xxs' align='center'>
              {author && (
                <Text variant='l1' color='subtext'>
                  By {author}
                </Text>
              )}
              {author && date && (
                <Separator
                  orientation='vertical'
                  className='h-4 bg-current opacity-30'
                />
              )}
              {date && (
                <Text variant='l1' color='subtext'>
                  {date}
                </Text>
              )}
            </Flex>
          )}
        </Card.Content>
      </>
    );

    // Wrap in link if href is provided
    if (href) {
      return (
        <Flex ref={ref} className='group'>
          <Link
            href={href}
            underline='none'
            className='block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-4 rounded-xl [&_h1]:transition-colors [&_h2]:transition-colors [&_h3]:transition-colors [&_h4]:transition-colors [&_h5]:transition-colors [&_h6]:transition-colors [&_h1]:group-hover:text-primary-700 [&_h2]:group-hover:text-primary-700 [&_h3]:group-hover:text-primary-700 [&_h4]:group-hover:text-primary-700 [&_h5]:group-hover:text-primary-700 [&_h6]:group-hover:text-primary-700'
            aria-label={`Link to: ${title}`}
          >
            <Card
              variant={cardVariant}
              border={border}
              className={articleCardContainerVariants({
                layout,
                gap: containerGap,
              })}
            >
              {cardContent}
            </Card>
          </Link>
        </Flex>
      );
    }

    // Otherwise, render as a Card
    return (
      <Card
        ref={ref}
        role='article'
        aria-label={title}
        variant={cardVariant}
        border={border}
        className={articleCardContainerVariants({
          layout,
          gap: containerGap,
        })}
      >
        {cardContent}
      </Card>
    );
  },
);

ArticleCard.displayName = 'ArticleCard';
